import { Inject, Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { concatMap, filter, last, map, switchMap, takeUntil } from 'rxjs/operators';

import { VALIDATORS, zipCodeValidator } from 'app/constants';
// import { FREE_PRICING_MAP } from 'app/constants/pricing.data';
import { absoluteValue } from 'app/utils/absolute-value';
import { getLinePricing } from 'app/utils/get-line-pricing';
import { isBomSubLine } from 'app/utils/is-bom-sub-line';
import { AppCustomLine as CustomLine, Offer, AppProductLine as ProductLine } from 'graphql/models/extended';
import { Address, ConditionType, Manufacturer } from 'graphql/models/generated';
import { ConditionTypeENUM } from 'models/condition.model';
import { OfferBase } from 'models/offer.model';
import { LineSubtypePropVal, PositionField, PositionUpdate, PositionUpdateInput } from 'models/position.model';
import {
  CustomPricing,
  ElementPricing,
  ElementPricingManufacturer,
  PriceDescriptor,
  ProductPricing
} from 'models/pricing.model';
import { ConfirmModalService } from './confirm-modal.service';
import { OfferPositionService } from 'shared/services/offer-position.service';
import { PositionCustomPropertiesService } from 'shared/services/position-custom-properties.service';
import { PositionValidationService } from 'shared/services/position-validation.service';
import { PricingService } from 'shared/services/pricing.service';
import { VariableAssignmentService } from 'shared/services/variable-assignment.service';

import { getResetDiscountDialogId } from 'app/utils/dialog-utils';
import { isDimensionsNoValue } from 'app/utils/is-custom-dimensions-value';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  constructor(
    private fb: UntypedFormBuilder,
    private offerPositionService: OfferPositionService,
    private posCustomPropertiesService: PositionCustomPropertiesService,
    private positionValidationService: PositionValidationService,
    private pricingService: PricingService,
    private confirmModalService: ConfirmModalService,
    private variableAssignmentService: VariableAssignmentService,
    @Inject('datamodel') public datamodel: Record<string, any>
  ) {}

  createAddressGroup(addr: Partial<Address>, required = false, disabled = false): UntypedFormGroup {
    const validators: Record<string, ValidatorFn[]> = {
      streetName: [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)],
      streetNr: [Validators.pattern(VALIDATORS.PATTERN.STREET_NR)],
      zipCode: [zipCodeValidator],
      city: [Validators.maxLength(VALIDATORS.MAX_LENGTH.INPUT)],
      country: [zipCodeValidator]
    };
    if (required) {
      Object.keys(validators).forEach((key) => {
        validators[key].push(Validators.required);
      });
    }

    return this.fb.group({
      id: [{ value: addr.id || '', disabled }],
      name: [{ value: addr.name || '', disabled }],
      streetName: [{ value: addr.streetName || '', disabled }, validators.streetName],
      streetNr: [{ value: addr.streetNr || '', disabled }, validators.streetNr],
      zipCode: [{ value: addr.zipCode || '', disabled }, validators.zipCode],
      city: [{ value: addr.city || '', disabled }, validators.city],
      country: [{ value: addr.country || null, disabled }, validators.country]
    });
  }

  getPositionBaseFormForMinicart(position?: ProductLine | CustomLine): UntypedFormGroup {
    return this.fb.group(
      {
        description: [(position && position.description) || ''],
        quantity: [
          (position && position.quantity && position.quantity.value) || '1',
          [this.positionValidationService.validateMinShort(1), this.positionValidationService.validateMaxShort(999)]
        ],
        pricing: new UntypedFormArray([])
      },
      { updateOn: 'blur' }
    );
  }

  getPositionBaseForm(position?: ProductLine | CustomLine): UntypedFormGroup {
    return this.fb.group(
      {
        number: [
          position ? this.posCustomPropertiesService.getPositionNumber(position) : '',
          [Validators.required, Validators.pattern(VALIDATORS.PATTERN.POSITION_NR)],
          position ? [this.positionValidationService.validatePositionNumber(position)] : []
        ],
        description: [(position && position.description) || ''],
        quantity: [
          (position && position?.quantity?.value) || '1',
          [this.positionValidationService.validateMinShort(1), this.positionValidationService.validateMaxShort(999)]
        ],
        pricing: new UntypedFormArray([])
      },
      { updateOn: 'blur' }
    );
  }

  getBomPositionBaseForm(position?: ProductLine | CustomLine): UntypedFormGroup {
    return this.fb.group(
      {
        quantity: [
          (position && position?.quantity?.value) || '1',
          [this.positionValidationService.validateMinShort(1), this.positionValidationService.validateMaxShort(99999)]
        ],
        pricing: new UntypedFormArray([])
      },
      { updateOn: 'blur' }
    );
  }

  detectPositionQuantityFormChanges(form: UntypedFormGroup, destroyed$: Subject<void>): void {
    form.controls.quantity.valueChanges
      .pipe(
        filter(() => Boolean(form.controls.quantity.valid)),
        takeUntil(destroyed$)
      )
      .subscribe((value) => {
        // empty quantity is not allowed, set it back to 1
        if (!value) {
          form.controls.quantity.setValue('1', { emitEvent: false });
        }
      });
  }

  private mapPricingFormChanges({
    isDealer,
    newValues,
    offer,
    oldValues,
    position
  }: {
    isDealer: boolean;
    newValues: Record<string, any>;
    offer: Offer | OfferBase;
    oldValues: Record<string, any>;
    position: ProductLine | CustomLine;
  }): PositionUpdateInput[] {
    const updates: PositionUpdateInput[] = [];
    const newPricing = newValues.pricing as Record<string, string>[];
    const oldPricing = this.getOldPricing(oldValues, offer, position, isDealer);
    const quantity = isBomSubLine(position) && position.quantity?.value ? position.quantity.value : newValues.quantity;

    return isDealer
      ? this.processDealerPricing(offer as Offer, position, updates, oldPricing, newPricing, quantity)
      : this.processManufacturerPricing(offer as Offer, position, updates, oldPricing, newPricing, quantity);
  }

  private getOldPricing(
    oldValues: Record<string, any>,
    offer: Offer | OfferBase,
    position: ProductLine | CustomLine,
    isDealer: boolean
  ): Record<string, string>[] {
    if (isDealer && this.isOfferWithPositionPricing(offer)) {
      const currentOffer = offer as Offer;

      if (currentOffer.positionPricingUpdate && currentOffer.positionPricingUpdate[position.id]) {
        const positionPricing = currentOffer.positionPricingUpdate[position.id] as ProductPricing;

        if (positionPricing.elements) {
          return this.extractPricingFromElements(positionPricing.elements, offer.calculationType);
        }

        return this.extractPositionPricing(
          currentOffer.positionPricingUpdate[position.id] as ProductPricing,
          offer.calculationType
        );
      }
    }

    return oldValues.pricing as Record<string, string>[];
  }

  private isOfferWithPositionPricing(offer: Offer | OfferBase): offer is Offer {
    return (offer as Offer).positionPricingUpdate !== undefined;
  }

  private extractPositionPricing(
    positionPricing: ProductPricing | CustomPricing,
    calculationType: ConditionType
  ): Record<string, string>[] {
    if ((positionPricing as ProductPricing).elements) {
      return this.extractPricingFromElements((positionPricing as ProductPricing).elements, calculationType);
    }

    return [this.extractPricingFromLine(positionPricing as CustomPricing, calculationType)] as Record<string, string>[];
  }

  private extractPricingFromElements(
    elements: ElementPricing[],
    calculationType: ConditionType
  ): Record<string, string>[] {
    return elements.map((element) => {
      if (calculationType === ConditionTypeENUM.SURCHARGE) {
        return {
          surchargeDiscount: (element.buyPriceSurcharge?.value ?? 0).toFixed(2),
          totalSellPrice: element.totalSellPrice.value.toFixed(2)
        };
      }

      return {
        surchargeDiscount: (element.listPriceDiscount?.value ?? 0).toFixed(2),
        totalSellPrice: element.totalSellPrice.value.toFixed(2)
      };
    });
  }

  private extractPricingFromLine(line: CustomPricing, calculationType: ConditionType): Record<string, string> {
    if (calculationType === ConditionTypeENUM.SURCHARGE) {
      return {
        surchargeDiscount: (line.buyPriceSurcharge?.value ?? 0).toFixed(2),
        totalSellPrice: line.totalSellPrice.value.toFixed(2)
      };
    }

    return {
      surchargeDiscount: (line.listPriceDiscount?.value ?? 0).toFixed(2),
      totalSellPrice: line.totalSellPrice.value.toFixed(2)
    };
  }

  private processDealerPricing(
    offer: Offer,
    position: ProductLine | CustomLine,
    updates: PositionUpdateInput[],
    oldPricingArray: Record<string, string>[],
    newPricingArray: Record<string, string>[],
    quantity: string
  ) {
    const currentOffer = offer as Offer;
    let linePricing: ElementPricing[] | CustomPricing[] | undefined;
    const isSurchargeCalculation = offer.calculationType === ConditionTypeENUM.SURCHARGE;

    if (currentOffer.positionPricingUpdate && currentOffer.positionPricingUpdate[position.id]) {
      linePricing = (currentOffer.positionPricingUpdate[position.id] as ProductPricing).elements
        ? (currentOffer.positionPricingUpdate[position.id] as ProductPricing).elements
        : [currentOffer.positionPricingUpdate[position.id] as CustomPricing];
    } else {
      linePricing = getLinePricing({ position, isDealer: true });
    }

    if (!linePricing) {
      return updates;
    }

    // eslint-disable-next-line consistent-return
    linePricing.forEach((pricing: CustomPricing | ElementPricing, i: number) => {
      const oldPricing = oldPricingArray[i];
      const newPricing = newPricingArray[i];

      // if (position.lineSubtype === LineSubtypePropVal.FREE_ARTICLE) {
      //   const oldBasePrice = oldPricing.basePrice;
      //   const newBasePrice = newPricing.basePrice;
      //   if (this.formatNumber(newBasePrice) !== this.formatNumber(oldBasePrice)) {
      //     updates.push({
      //       key: PositionField.PRICING,
      //       value: {
      //         lineId: FREE_PRICING_MAP.basePrice,
      //         stepId: FREE_PRICING_MAP.basePrice,
      //         value: Number(newBasePrice)
      //       }
      //     });
      //
      //     return updates;
      //   }
      // }

      const oldSurchargeDiscount = oldPricing.surchargeDiscount;
      const newSurchargeDiscount = newPricing.surchargeDiscount;

      const oldTotalSell = oldPricing.totalSellPrice;
      const newTotalSell = newPricing.totalSellPrice;

      if (this.formatNumber(newTotalSell) !== this.formatNumber(oldTotalSell)) {
        const update = this.calculateSurchargeDiscount(
          Number(this.formatNumber(newTotalSell)),
          Number(quantity),
          pricing,
          isSurchargeCalculation,
          position.lineSubtype
        );
        if (update) {
          updates.push({ key: PositionField.PRICING, value: update });
        }
      }

      if (this.formatNumber(newSurchargeDiscount) !== this.formatNumber(oldSurchargeDiscount)) {
        if (isSurchargeCalculation) {
          if (
            pricing.buyPriceSurcharge &&
            this.formatNumber(pricing.buyPriceSurcharge.value) !== this.formatNumber(newSurchargeDiscount)
          ) {
            updates.push({
              key: PositionField.PRICING,
              value: {
                ...pricing.buyPriceSurcharge,
                value: Number(newSurchargeDiscount)
              }
            });
          }

          return updates;
        }

        if (
          pricing.listPriceDiscount &&
          this.formatNumber(absoluteValue(pricing.listPriceDiscount.value)) !== this.formatNumber(newSurchargeDiscount)
        ) {
          updates.push({
            key: PositionField.PRICING,
            value: {
              ...pricing.listPriceDiscount,
              value: Number(this.formatNumber(newSurchargeDiscount))
            }
          });

          return updates;
        }
      }
    });

    return updates;
  }

  private processManufacturerPricing(
    offer: Offer,
    position: ProductLine | CustomLine,
    updates: PositionUpdateInput[],
    oldPricingArray: Record<string, string>[],
    newPricingArray: Record<string, string>[],
    quantity: string
  ) {
    let linePricing: ElementPricing[] | CustomPricing[] | undefined;
    if (offer.positionPricingUpdate && offer.positionPricingUpdate[position.id]) {
      linePricing = (offer.positionPricingUpdate[position.id] as ProductPricing).elements
        ? (offer.positionPricingUpdate[position.id] as ProductPricing).elements
        : [offer.positionPricingUpdate[position.id] as CustomPricing];
    } else {
      linePricing = getLinePricing({ position, isDealer: false });
    }

    if (!linePricing) {
      return updates;
    }

    linePricing.forEach((pricing: CustomPricing | ElementPricing, i: number) => {
      const oldPricing = oldPricingArray[i];
      const newPricing = newPricingArray[i];
      const oldTotalSell = oldPricing.totalSellPrice;
      const newTotalSell = newPricing.totalSellPrice;

      if (this.formatNumber(oldPricing.tz) !== this.formatNumber(newPricing.tz)) {
        // For manufacturer, we only update the TZ value
        // if it was changed by the user, not by the system
        // (e.g. changed for all rows at the sentence level)
        // also allow this when the old TZ value seems stale
        const pricingTz = pricing?.tz ? pricing.tz.value : null;
        if (pricingTz === null || this.formatNumber(pricingTz) !== this.formatNumber(newPricing.tz)) {
          updates.push({
            key: PositionField.TZ,
            value: { ...pricing.tz, value: this.formatNumber(newPricing.tz) }
          });

          return updates;
        }
      }

      if (position.lineSubtype === LineSubtypePropVal.FREE_ARTICLE) {
        const oldTotalBase = oldPricing.totalBasePrice;
        const newTotalBase = newPricing.totalBasePrice;
        // for manufacturer, we only update basePrice if the
        // totalBasePrice or basePrice was changed by the user
        // and we don't need to update discounts because
        // Free Articles don't have discount on manufacturer side
        if (
          this.formatNumber(newTotalBase) !== this.formatNumber(oldTotalBase)
        ) {

          const discountUpdates = this.calculateAlternateDiscount(
            Number(newTotalBase),
            Number(quantity),
            pricing as ElementPricingManufacturer
          );

          if (discountUpdates) {
            // eslint-disable-next-line no-param-reassign
            updates = updates.concat(
              discountUpdates.map((du) => ({
                key: PositionField.PRICING,
                value: du
              }))
            );

            return updates;
          }
        }
      }

      if (this.formatNumber(newTotalSell) !== this.formatNumber(oldTotalSell)) {
        // for manufacturer, we only calculate the alternate discount if the
        // totalSellPrice was changed by the user, not by the system
        // (e.g. if we change TZ, the totalSellPrice will be changed by the system)
        // (e.g. some other discount changed)
        // also allowing this when the oldValue seems stale
        if (this.formatNumber(pricing.totalSellPrice.value) !== this.formatNumber(newTotalSell)) {
          const discountUpdates = this.calculateAlternateDiscount(
            Number(newTotalSell),
            Number(quantity),
            pricing as ElementPricingManufacturer
          );
          if (discountUpdates) {
            // eslint-disable-next-line no-param-reassign
            updates = updates.concat(
              discountUpdates.map((du) => ({
                key: PositionField.PRICING,
                value: du
              }))
            );

            return updates;
          }
        }
      }
    });

    return updates;
  }

  private mapFormChangesToPositionUpdates(changes: {
    isDealer: boolean;
    newValues: Record<string, any>;
    offer: Offer | OfferBase;
    oldValues: Record<string, any>;
    position: ProductLine | CustomLine;
  }): PositionUpdateInput[] {
    const { newValues, oldValues, position } = changes;

    // Note:
    // `oldValues` seem to be stale and not updated when using `setValue`.
    // Since we do update ArticleID and positionNumber in `applyPositionUpdates`,
    // we can use that instead

    // We don't need to check number, quantity, description
    // and measurements fields for BOM lines

    if (!isBomSubLine(position)) {
      if (newValues.number && Number(newValues.number) !== Number(oldValues.number)) {
        return [{ key: PositionField.NUMBER, value: newValues.number }];
      }

      if (
        newValues.description !== undefined &&
        newValues.description !== null &&
        newValues.description !== oldValues.description
      ) {
        return [{ key: PositionField.DESCRIPTION, value: newValues.description }];
      }

      if (newValues.quantity && Number(newValues.quantity) !== Number(oldValues.quantity)) {
        return [{ key: PositionField.QUANTITY, value: Number(newValues.quantity) }];
      }

      const dmDimensions = this.datamodel.dimensions;

      const posUpdate: PositionUpdateInput[] = [];

      const dimensions = (Object.values(dmDimensions) as string[]).filter((dim) => dim.length === 2);
      dimensions.forEach(([ctrlInputId, ctrlSelectId]) => {
        const { [ctrlInputId]: oldInputValue = '', [ctrlSelectId]: oldSelectValue = '' } = oldValues;
        const { [ctrlInputId]: newInputValue = '', [ctrlSelectId]: newSelectValue = '' } = newValues;

        // the condition also checks if select_value has a prefix ('UZ_' etc)
        if (oldSelectValue !== newSelectValue && !oldSelectValue.includes('_')) {
          posUpdate.push({
            key: PositionField.DIMENSION,
            value: newSelectValue,
            dimensionKey: ctrlSelectId
          });
        }

        if (oldInputValue !== newInputValue) {
          posUpdate.push({
            key: PositionField.CUSTOM_DIMENSION,
            value: newInputValue,
            dimensionKey: ctrlInputId
          });
        }
      });

      if (posUpdate.length) {
        return posUpdate;
      }

      const directions = (Object.values(dmDimensions) as string[]).filter(
        (dim) => typeof dim === 'string' && dim !== ''
      );
      directions.forEach((key) => {
        const oldInputValue = oldValues[key];
        const newInputValue = newValues[key];

        if (oldInputValue !== newInputValue) {
          posUpdate.push({
            key: PositionField.DIRECTION,
            value: newInputValue,
            dimensionKey: key
          });
        }
      });

      if (posUpdate.length) {
        return posUpdate;
      }
    }

    if (newValues.pricing !== oldValues.pricing) {
      return this.mapPricingFormChanges(changes);
    }

    return [];
  }

  onPositionFormChanges(changes: {
    isDealer: boolean;
    newValues: Record<string, any>;
    offer: Offer | OfferBase;
    oldValues: Record<string, any>;
    position: ProductLine | CustomLine;
    manufacturer?: Manufacturer;
    withPricing?: boolean;
    withQuotePricing?: boolean;
  }): Observable<PositionUpdate>[] {
    return this.mapFormChangesToPositionUpdates(changes).map((input) => {
      return this.onUpdatePosition(
        changes.offer,
        changes.position.id,
        input,
        changes.isDealer,
        changes.withPricing,
        changes.withQuotePricing
      ).pipe(map((offer) => ({ offer, input })));
    });
  }

  private calculateSurchargeDiscount(
    totalSellPrice: number,
    quantity: number,
    pricing: ElementPricing | CustomPricing,
    isSurchargeCalculation: boolean,
    lineType?: string
  ): PriceDescriptor | undefined {
    if (isSurchargeCalculation) {
      if (pricing.buyPriceSurcharge) {
        const totalBasePrice = pricing.totalBasePrice?.value || 0;
        const surcharge = (totalSellPrice / totalBasePrice - 1) * 100;
        if (
          this.formatNumber(absoluteValue(pricing.buyPriceSurcharge.value)) !==
          this.formatNumber(absoluteValue(surcharge))
        ) {
          return { ...pricing.buyPriceSurcharge, value: surcharge };
        }
      }

      return undefined;
    }

    if (pricing.listPriceDiscount) {
      const priceValue =
        lineType && lineType === LineSubtypePropVal.MY_ARTICLE
          ? (pricing.basePrice && pricing.basePrice.value) || 0
          : (pricing.listPrice && pricing.listPrice.value) || 0;
      const totalListPrice = priceValue * quantity;
      const discount = absoluteValue(totalSellPrice / totalListPrice - 1) * -100;

      if (this.formatNumber(pricing.listPriceDiscount.value) !== this.formatNumber(discount)) {
        return { ...pricing.listPriceDiscount, value: discount };
      }
    }

    return undefined;
  }

  /** This is assumed to be triggered on changing the total sell price */
  private calculateAlternateDiscount(
    totalSellPrice: number,
    quantity: number,
    pricing: ElementPricingManufacturer
  ): PriceDescriptor[] | undefined {
    const updates: PriceDescriptor[] = [];

    if (!pricing.alternativeDiscount) {
      return updates;
    }

    const discount = this.pricingService.calculateManufacturerAlternativeDiscount({
      listPrice: pricing.listPrice.value,
      positionDiscount: (pricing.positionDiscount && pricing.positionDiscount.value) || 0,
      tz: (pricing.tz && pricing.tz.value) || 0,
      quantity,
      totalSellPrice
    });

    if (this.formatNumber(pricing.alternativeDiscount.value) !== this.formatNumber(discount)) {
      updates.push({
        ...pricing.alternativeDiscount,
        value: discount ? -absoluteValue(Number(this.formatNumber(discount))) : 0
      });
    }

    return updates.length > 0 ? updates : undefined;
  }

  private onUpdatePosition(
    offer: OfferBase,
    positionId: string,
    input: PositionUpdateInput,
    isDealer?: boolean,
    withPricing?: boolean,
    withQuotePricing?: boolean
  ): Observable<Offer> {
    switch (input.key) {
      case PositionField.NUMBER:
        return this.offerPositionService.updatePositionNumber(offer, positionId, input.value);

      case PositionField.DESCRIPTION: {
        const inputText = input.value.slice(0, VALIDATORS.MAX_LENGTH.LV_POSITION_TEXT);
        return this.offerPositionService.updatePositionDescription(offer, positionId, inputText);
      }

      case PositionField.QUANTITY: {
        if (isDealer && (offer as any).lines) {
          const vars = this.getResetDiscountsVars(offer, positionId);

          if (vars.dialogId) {
            return this.confirmModalService
              .show(vars.dialogId, { cancel: 'Nein' })
              .onAction()
              .pipe(
                switchMap((action) => {
                  if (action) {
                    const observables = this.pricingService.getResetDiscountsObservables(
                      offer as Offer,
                      vars.resetOfferDiscount,
                      vars.resetPosDiscount,
                      vars.line
                    );

                    return from(observables).pipe(
                      concatMap(obs => obs),
                      last(),
                      switchMap(() =>
                        this.offerPositionService.updatePositionQuantity(
                          offer,
                          positionId,
                          input.value,
                          withPricing,
                          withQuotePricing
                        )
                      )
                    );
                  } else {
                    return throwError(() => new Error('User did not confirm the action.'));
                  }
                })
              );
          }

          return this.offerPositionService.updatePositionQuantity(
            offer,
            positionId,
            input.value,
            withPricing,
            withQuotePricing
          );
        }

        return this.offerPositionService.updatePositionQuantity(
          offer,
          positionId,
          input.value,
          withPricing,
          withQuotePricing
        );
      }

      case PositionField.TZ:
      case PositionField.PRICING:
        return this.pricingService.updatePricing(
          offer,
          positionId,
          input.value,
          withPricing
        );

      case PositionField.DIRECTION:
      case PositionField.DIMENSION:
      case PositionField.CUSTOM_DIMENSION: {
        if (isDealer) {
          const vars = this.getResetDiscountsVars(offer, positionId);

          if (vars.dialogId) {
            return this.confirmModalService
              .show(vars.dialogId, { cancel: 'Nein' })
              .onAction()
              .pipe(
                switchMap((action) => {
                  if (action) {
                    const observables = this.pricingService.getResetDiscountsObservables(
                      offer as Offer,
                      vars.resetOfferDiscount,
                      vars.resetPosDiscount,
                      vars.line
                    );

                    return from(observables).pipe(
                      concatMap(obs => obs),
                      last(),
                      switchMap(() =>
                        this.onUpdatePositionMeasurements(
                          offer,
                          positionId,
                          input,
                          withPricing
                        )
                      )
                    );
                  }

                  return of(offer as Offer);
                })
              );
          } else {
            return this.onUpdatePositionMeasurements(offer, positionId, input, withPricing);
          }
        } else {
          return this.onUpdatePositionMeasurements(offer, positionId, input, withPricing);
        }
      }

      default:
        return of(offer as Offer);
    }
  }

  private getResetDiscountsVars(offer: Offer | OfferBase, positionId: string) {
    const currentOffer = offer as Offer;
    const line = (
      currentOffer.lines!.find((position) => position.id === positionId) ||
      this.offerPositionService.findProductPositionLine(currentOffer.productPositions, positionId)
    );
    const lineType = (line  ? line.lineSubtype || this.posCustomPropertiesService.getLineSubtype(line) : undefined);

    const elements =
      currentOffer.positionPricingUpdate && currentOffer.positionPricingUpdate[positionId]
        ? (currentOffer.positionPricingUpdate[positionId] as ProductPricing).elements
        : (line!.pricing as ProductPricing).elements;

    const resetOfferDiscount = this.pricingService.hasOfferManufacturerDiscounts(offer);
    const resetPosDiscount =
      this.pricingService.hasPositionManufacturerDiscounts(elements) ||
      Boolean(currentOffer.pricing?.additionalDiscount?.isChangedManually) ||
      Boolean(currentOffer.pricing?.alternativeDiscount?.isChangedManually) ||
      Boolean(currentOffer.pricing?.positionDiscount?.isChangedManually); // line discounts can also appear in the case when offer header discounts are modified
    let dialogId = (lineType !== LineSubtypePropVal.MY_ARTICLE
      ? getResetDiscountDialogId(resetOfferDiscount, resetPosDiscount)
      : undefined);

    return { line, elements, resetOfferDiscount, resetPosDiscount, dialogId };
  }

  private onUpdatePositionMeasurements(
    offer: Offer | OfferBase,
    positionId: string,
    input: PositionUpdateInput,
    withPricing?: boolean
  ): Observable<Offer> {
    return input.dimensionKey && !isDimensionsNoValue(input.value)
      ? this.variableAssignmentService.updatePositionMeasurements(
          offer as Offer,
          positionId,
          input.dimensionKey,
          input.value,
          input.key,
          withPricing
        )
      : of(offer as Offer);
  }

  private formatNumber(num: string | number): string {
    return Number(num).toFixed(2);
  }
}
